import { useMutation, type UseMutationOptions } from '@tanstack/react-query'

import { customFetch, type CustomFetchOptions } from './custom-fetch'
import type { UploadedImage } from './generated/model'

/**
 * Sending a file - FRONTEND.md §9.796.
 *
 * <p>**Six generated functions cannot do it.** orval writes a file part as a `Blob` field and sends
 * the body as `JSON.stringify(body)`, unless the server's spec declares the request
 * `multipart/form-data`. `/upload-image`, `/upload-media`, `/auth/me/avatar`, `/stock/receipts/ocr`
 * and `/restore-database` declare nothing, and `/apk/upload-direct` declares a binary body that
 * orval stringifies anyway. A `File` and a `FormData` both serialise to `{}`, so each of those
 * sends two characters and no file - the owner's *"Žiadny súbor nebol nahraný"* on the design
 * page, and a driver app overwritten by `{}` on the APK screen.
 *
 * <p>So a file goes through here, and a guard (`NoFileIsSentAsJsonTest`) refuses the six generated
 * functions anywhere outside the generated tree.
 */

/** One file as one part of a real `multipart/form-data` body, named `field`. */
export function postFile<T>(
  path: string,
  field: string,
  file: Blob,
  options?: CustomFetchOptions,
): Promise<T> {
  const body = new FormData()
  body.append(field, file)
  // No content type: the browser writes `multipart/form-data` with the boundary it chose, and a
  // hand-written one would carry no boundary at all.
  return customFetch<T>(path, { ...options, method: 'POST', body })
}

/** The file itself as the request body, for an endpoint that reads the raw bytes. */
export function postRawFile<T>(
  path: string,
  file: Blob,
  contentType: string,
  options?: CustomFetchOptions,
): Promise<T> {
  const headers = new Headers(options?.headers)
  headers.set('content-type', contentType)
  return customFetch<T>(path, { ...options, method: 'POST', headers, body: file })
}

/** A picture for the menu, the homepage or a burger-box slot: `POST /api/v1/upload-image`. */
export function uploadImage(file: Blob): Promise<Partial<UploadedImage> | null> {
  return postFile<Partial<UploadedImage> | null>('/api/v1/upload-image', 'image', file)
}

/**
 * {@link uploadImage} as a mutation, so a screen keeps its `isPending`, `onSuccess` and `onError`
 * where `usePostUploadImage` gave them.
 */
export function useUploadImage(
  options?: Omit<
    UseMutationOptions<Partial<UploadedImage> | null, unknown, Blob>,
    'mutationFn'
  >,
) {
  return useMutation({ ...options, mutationFn: (file: Blob) => uploadImage(file) })
}
